// TODO(CORE-6408): Remove this eslint-disable once the upload functionality is implemented and the console logs are removed.
/* eslint-disable no-console */
import { useState, type VoidFunctionComponent } from 'react';

import { FormLabelVariant, LabeledTextInput } from '../../generic';
import Button, { LoaderButton } from '../../generic/Button';
import UploadFile from '../../generic/Forms/UploadFile';

import styles from './UploadDependency.module.less';

export type Props = {
  /**
   * Whether the dependency is being added as a vendored dependency.
   */
  isVendored?: boolean;

  /**
   * Whether the dependency is currently being saved.
   */
  isSaving?: boolean;

  /**
   * Callback when the drawer is closed.
   */
  onClose?: () => void;
};

const UploadDependency: VoidFunctionComponent<Props> = ({
  isVendored,
  isSaving,
  onClose,
}) => {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [url, setURL] = useState<string>('');

  const [filePath, setFilePath] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [version, setVersion] = useState<string>('');
  const [homepage, setHomepage] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const canSaveFileOrURL = !!file || !!url;
  const canSaveVendored = isVendored ? !!filePath : true;
  const canSave = !!name && !!version && canSaveFileOrURL && canSaveVendored;

  const onUpload = async () => {
    // NOTE: This will only ever be called if canSave is true but TS doesn't know that.
    if (!canSave) {
      return;
    }

    // TODO(CORE-6408): Remove the console logs and replace with actual upload logic.
    console.log('Uploading dependency with the following details:');
    console.log('File:', file);
    console.log('URL:', url);
    console.log('Name:', name);
    console.log('Version:', version);
    console.log('Homepage:', homepage);
    console.log('Description:', description);
    console.log('File Path (for vendored):', filePath);

    onClose?.();
  };

  return (
    <section>
      <header>
        <p className={styles.overview}>
          Upload an archive from your computer or a URL to add a {isVendored ? 'vendored' : 'manual'} dependency to your project.
        </p>
      </header>

      <main className={styles.main}>
        <h3 className={styles.title}>
          Choose File
        </h3>
        <UploadFile
          file={file}
          setFile={setFile}
          url={url}
          setURL={setURL}
          isDisabled={isSaving}
        />

        <h3 className={styles.title}>
          Package Details
        </h3>
        {isVendored && (
          <div className={styles.field}>
            <LabeledTextInput
              id="upload-dependency-file-path"
              size="small"
              label="File Path"
              labelVariant={FormLabelVariant.Filter}
              placeholder="Path to the vendored dependency"
              value={filePath}
              onChange={(value) => setFilePath(value)}
              isRequired
            />
            <p className={styles.notes}>Add the path to the vendored dependency.</p>
          </div>
        )}

        <div className={styles.field}>
          <LabeledTextInput
            id="upload-dependency-name"
            size="small"
            label="Name"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a package name"
            value={name}
            onChange={(value) => setName(value)}
            isRequired
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="upload-dependency-version"
            size="small"
            label="Version"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a version number, eg. v1 or 1.0"
            value={version}
            onChange={(value) => setVersion(value)}
            isRequired
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="upload-dependency-homepage"
            size="small"
            label="Homepage"
            labelVariant={FormLabelVariant.Filter}
            placeholder="https://dependency.com"
            value={homepage}
            onChange={(value) => setHomepage(value)}
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="upload-dependency-description"
            size="small"
            label="Description"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a description"
            value={description}
            onChange={(value) => setDescription(value)}
          />
        </div>

        <div className={styles.infoCard}>
          <p className={styles.infoCardHeading}>Uploaded dependencies aren&apos;t validated by FOSSA</p>
          <p className={styles.infoCardText}>
            We scan the uploaded files for licensing data, but package details
            can&apos;t be verified and are saved as provided.
          </p>
        </div>
      </main>

      <footer className={styles.footer}>
        <Button type="secondary" onClick={() => onClose?.()}>Cancel</Button>
        <LoaderButton
          type="primary"
          onClick={onUpload}
          isDisabled={!canSave}
          isLoading={isSaving}
        >
          Upload
        </LoaderButton>
      </footer>
    </section>
  );
};

export default UploadDependency;
